// Making a line's voice: the line goes onto the timeline at once as a "syncing" line (with an estimated length), then
// the voice is made (the AI speaks the text, or the recording is used), cleaned up and lip-synced here, and the
// line is updated quietly by id (so undo never brings back a half-finished one). Its real length replaces the estimate,
// which moves every later beat and its markers. The timeline version of src/ui/episode/lines.ts `makeLine`.
import { lipSyncUrl } from "../../../audio/lipsync";
import { getLibrary } from "../../../library/store";
import { uid } from "../../../model/ids";
import type { Take } from "../../../model/episode";
import type { Emotion } from "../../../model/face";
import { findAsset } from "../../../model/library";
import { addLine, addMarker, lineById, setLine, type TimelineEpisode } from "../../../model/timeline";
import { api, audioUrl } from "../../api";
import type { Update } from "../episodeStore";
import { applyToEpisode } from "../episodeStore";
import { notify } from "../../jobs";

export type ClipSource = { blob: Blob; seconds: number } | { text: string; direction?: string };

// A guess at how long a typed line takes to say, so the clip has width while the voice is being made.
export const estimateSeconds = (text: string) => 0.5 + 0.06 * text.length;

// Puts a new line at t (see addLine for where it goes; feeling: a feeling marker at the line's start), or remakes the
// line `editId` (a silent one becomes a spoken line). Returns the line's id and, for a new line, its start, so the editor can move the playhead there.
// `emotion` is how the AI voice should sound. `done` is told when it has been made, or what went wrong.
export function makeClip(ep: TimelineEpisode, update: Update<TimelineEpisode>, o: {
  char: string; t: number; editId?: string; id?: string; source: ClipSource; emotion: Emotion; feeling?: Emotion; done?: (error?: string) => void; // id: a new line's id
}): { id: string; start?: number } {
  const { char, source, emotion } = o;
  const typed = "text" in source;
  const text = typed ? source.text.trim() : undefined;
  const direction = typed ? source.direction?.trim() || undefined : undefined;
  const id = o.editId ?? o.id ?? uid("ln");
  const guess = typed ? estimateSeconds(text!) : source.seconds;
  let start: number | undefined;

  if (o.editId) {
    update(e => setLine(e, id, {
      char, how: typed ? "typed" : "recorded", status: "syncing", // (a silent line becomes spoken in place, same id) error: undefined, text, direction,
      duration: lineById(e, id)?.duration || guess,
    }));
  } else {
    const insert = (e: TimelineEpisode) => {
      const r = addLine(e, o.t, { id, char, how: typed ? "typed" : "recorded", status: "syncing", text, direction, duration: guess, cues: [], takes: [] });
      return { start: r.start, ep: o.feeling ? addMarker(r.ep, "feeling", r.start, { char, emotion: o.feeling }) : r.ep };
    };
    const first = insert(ep);
    start = first.start;
    update(e => (e === ep ? first.ep : insert(e).ep));
  }

  void (async () => {
    try {
      const r = typed
        ? await api.speakLine(ep.id, id, { text: text!, speech: findAsset(getLibrary(), char)?.speech ?? { voice: "Puck", style: "" }, emotion, direction })
        : await api.uploadTake(ep.id, id, source.blob);
      const cues = await lipSyncUrl(audioUrl(ep.id, r.audio));
      await applyToEpisode(ep.id, e => {
        const old = lineById(e, id);
        const take: Take = { id: uid("t"), at: Date.now(), audio: r.audio, duration: r.duration, cues, text, emotion, direction };
        return setLine(e, id, { status: "ready", error: undefined, audio: r.audio, duration: r.duration, cues, takes: [...(old?.takes ?? []), take] });
      });
      o.done?.();
    } catch (err) {
      const message = (err as Error).message;
      // The line shows the error. If even that can't be written, say so on whatever screen is open.
      await applyToEpisode(ep.id, e => setLine(e, id, { status: "error", error: message })).catch((e: Error) => notify(`Couldn't make the line's voice: ${message} (${e.message})`));
      o.done?.(message);
    }
  })();
  return { id, start };
}
